import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Bell, Package, GraduationCap, Search, X } from "lucide-react";

const TIPO_META = {
  iscrizione: { label: "Iscrizione", icon: GraduationCap, cls: "bg-dusty-50 text-dusty-700 border-dusty-200" },
  ordine: { label: "Ordine", icon: Package, cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

export default function Notifiche() {
  const [items, setItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ partner_id: "all", tipo: "all", q: "", dal: "", al: "" });

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 500 };
    if (f.partner_id !== "all") params.partner_id = f.partner_id;
    if (f.tipo !== "all") params.tipo = f.tipo;
    if (f.q.trim()) params.q = f.q.trim();
    if (f.dal) params.dal = f.dal;
    if (f.al) params.al = f.al;
    api.get("/notifications", { params })
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [f]);

  useEffect(() => { api.get("/partners").then((r) => setPartners(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => setF({ partner_id: "all", tipo: "all", q: "", dal: "", al: "" });
  const hasFilters = f.partner_id !== "all" || f.tipo !== "all" || f.q || f.dal || f.al;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="notifiche-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><Bell className="h-7 w-7 text-dusty-600" /> Storico Notifiche</h1>
        <p className="text-sm text-slate-500 mt-1">Archivio di iscrizioni e ordini ricevuti dai partner. Filtra per partner, corso o periodo.</p>
      </div>

      <Card className="border-slate-200 shadow-sm p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">Partner</Label>
            <Select value={f.partner_id} onValueChange={(v) => setF((s) => ({ ...s, partner_id: v }))}>
              <SelectTrigger className="mt-1.5 bg-white" data-testid="notif-filter-partner"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i partner</SelectItem>
                {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={f.tipo} onValueChange={(v) => setF((s) => ({ ...s, tipo: v }))}>
              <SelectTrigger className="mt-1.5 bg-white" data-testid="notif-filter-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="iscrizione">Iscrizioni</SelectItem>
                <SelectItem value="ordine">Ordini</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Corso / testo</Label>
            <div className="relative mt-1.5">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input value={f.q} onChange={(e) => setF((s) => ({ ...s, q: e.target.value }))} placeholder="Cerca corso…" className="pl-8 bg-white" data-testid="notif-filter-q" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Dal</Label>
            <Input type="date" value={f.dal} onChange={(e) => setF((s) => ({ ...s, dal: e.target.value }))} className="mt-1.5 bg-white" data-testid="notif-filter-dal" />
          </div>
          <div>
            <Label className="text-xs">Al</Label>
            <Input type="date" value={f.al} onChange={(e) => setF((s) => ({ ...s, al: e.target.value }))} className="mt-1.5 bg-white" data-testid="notif-filter-al" />
          </div>
        </div>
        {hasFilters && (
          <div className="mt-3">
            <Button variant="ghost" size="sm" onClick={reset} className="text-slate-500" data-testid="notif-filter-reset"><X className="h-3.5 w-3.5 mr-1" /> Azzera filtri</Button>
          </div>
        )}
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm text-slate-500" data-testid="notif-count">
          {loading ? "Caricamento…" : `${items.length} notifiche`}
        </div>
        <div className="divide-y divide-slate-100">
          {items.map((n) => {
            const meta = TIPO_META[n.tipo] || { label: n.tipo, icon: Bell, cls: "bg-slate-50 text-slate-600 border-slate-200" };
            const Icon = meta.icon;
            return (
              <div key={n.id} className="flex items-start gap-3 px-4 py-3" data-testid={`notif-row-${n.id}`}>
                <div className="h-9 w-9 rounded-lg bg-slate-50 flex items-center justify-center shrink-0 text-slate-500"><Icon className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded-full border font-medium ${meta.cls}`}>{meta.label}</span>
                    {n.partner_nome && <span className="text-xs text-slate-500">{n.partner_nome}</span>}
                    {!n.letto && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-600 font-semibold">Nuovo</span>}
                  </div>
                  <div className="text-sm text-slate-800 mt-1 leading-snug">{n.messaggio}</div>
                </div>
                <div className="text-[11px] text-slate-400 shrink-0 whitespace-nowrap">{new Date(n.created_at).toLocaleString("it-IT")}</div>
              </div>
            );
          })}
          {!loading && items.length === 0 && (
            <div className="py-16 text-center text-slate-400" data-testid="notif-empty">Nessuna notifica corrisponde ai filtri.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
